// AICA Dashboard - Main Dashboard Screen
// Priority 2.7: Role-optimized dashboards for different user roles
// Priority 3.2: Lazy loading for performance optimization
// Priority 2.1: Onboarding checklist & product tour integration
import { createFileRoute, Link } from '@tanstack/react-router';
import { useRole } from '@/contexts/RoleContext';
import { DashboardSkeleton } from '@/components/ui/skeletons';
import {
  OnboardingChecklist,
  OnboardingTour,
  clearPendingTour,
} from '@/components/onboarding';
import { useTranslation } from 'react-i18next';
import { useState, lazy, Suspense, useEffect, startTransition } from 'react';
import { AlertTriangle, ArrowRight, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { logWarning, logError, logDebug } from '@/lib/logger';
import { DemoDataCard } from '@/components/demo-data/DemoDataCard';
import { AnimatedContainer } from '@/components/transitions';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { useConsentStatus } from '@/features/onboarding/useConsentStatus';
import { dashboardFor, managedAccessOf } from '@/lib/route-permissions';
import { CONTROL_SQUARE } from '@/components/ui/control-size';
import { useUnifiedShellView } from '@/components/dashboards/unified/useUnifiedShellView';

// Lazy load dashboard components (code splitting)
const OwnerDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.OwnerDashboard }))
);
const ClientDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.ClientDashboard }))
);
const AdminDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.AdminDashboard }))
);
const AgentDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.AgentDashboard }))
);
const SupervisorDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.SupervisorDashboard }))
);
// The unified dashboard's shell, closed preview (#1337).
const UnifiedDashboardPage = lazy(
  () => import('@/components/dashboards/unified/UnifiedDashboardPage')
);

function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'common', 'settings']);
  useDocumentTitle(t('common:nav.dashboard', 'Overview'));
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCollapsed, setOnboardingCollapsed] = useState(false);
  const { globalRole, isOwner, isLoading: roleLoading, viewAsRole, access } = useRole();

  // Compliance status for the banner. useConsentStatus is role-gated: agent/
  // supervisor resolve fail-open without a network call (RBAC would 403 them),
  // and fail-open hides the banner — acting on it needs client/admin anyway.
  const { data: complianceStatusData } = useConsentStatus();

  const complianceLoading = !complianceStatusData;
  const complianceConfirmed =
    complianceLoading || (complianceStatusData?.complianceConfirmed ?? false);

  // Use effectiveRole for UI rendering (viewAsRole if set, otherwise globalRole)
  // This allows owner to preview dashboards as different roles
  const effectiveRole = viewAsRole || globalRole;
  // The dashboard on screen: the role's, or — once `/access/me` says so — the
  // one a grant covers (`dashboardFor`, #1645).
  const shownDashboard = dashboardFor(effectiveRole, access);
  // The closed preview of the unified dashboard (#1337): on, it replaces the
  // client, admin and supervisor dashboards — a grant holder's included — and
  // the agent's, by the shell's personal view «Моё» (#1338 PR C); the platform
  // owner keeps theirs. Off, nothing changes. Decided by
  // the flag map as it stands (the localStorage seed or the network's answer):
  // nothing waits for `/feature-flags` — the preview is closed for nearly
  // everyone, so a skeleton would be paid for by all of them. No answer (no
  // organisation, a failed read) is «off». Right after the platform owner
  // toggles the preview for an organisation a stale seed may swap the screen
  // when the answer lands; that is accepted for a platform-owner preview.
  // The personal view reads the CALLER's own records, so the platform owner
  // keeps their dashboard, and in «View As» the agent's stays the role
  // dashboard (`useUnifiedShellView`).
  const unifiedView = useUnifiedShellView();
  // No organisation selected: the flags are never asked, nothing will answer.
  const { isReady, orgId } = useFeatureFlagsSafe();
  const flagsSettled = isReady || !orgId;
  const unifiedShown = unifiedView !== null;
  // Onboarding follows the dashboard on screen (#1677). The checklist is the
  // ROLE's own work (create projects, invite users, export reports …): it is
  // offered while the dashboard on screen is the role's, and not to a grant
  // holder the grant gave another dashboard — a group lead whose base role is
  // `agent` on the team dashboard, an analyst with a supervisor base on the
  // admin overview — whose role's tasks would describe somebody else's screens
  // and whose dashboard's tasks its role may not do. Without a grant the two
  // are always the same: nothing changes.
  const onboardingRole = shownDashboard === effectiveRole ? effectiveRole : null;
  // Which dashboard is on screen is known once `/access/me` has answered, or
  // when nothing will answer (`failed` without `retry`: nothing was asked —
  // the role decides for good). While it loads, or after a read that failed
  // with a retry on offer, a grant may still replace the dashboard: the tour
  // and the checklist wait (code-review #1705) — the tour would auto-start and
  // be marked seen, the checklist flash, over a dashboard about to change.
  // A platform owner never holds a grant: nothing to wait for.
  //
  // The tour and the checklist point at the ROLE dashboard's widgets, so they
  // are never mounted over the unified shell. The checklist does not wait for
  // `/feature-flags` (code-review #1843): that wait would delay onboarding in
  // every organisation for a preview almost none has. The tour alone does —
  // it is marked seen the moment it starts, so with no seed and no answer yet
  // it holds (`screenSettled`) rather than start over a dashboard the preview
  // may replace a moment later (#2581).
  const dashboardKnown =
    !unifiedShown &&
    (isOwner ||
      access.status === 'resolved' ||
      (access.status === 'failed' && !access.retry));

  // The shell has no tour: a request queued for `/dashboard` (the help menu's
  // «Пройти тур» from another page, or from before the preview was on) must not
  // wait there to start on its own when the preview is switched off (#2581).
  useEffect(() => {
    if (unifiedShown) clearPendingTour();
  }, [unifiedShown]);

  // Get storage key for onboarding (owner uses admin tasks, but in View As mode use effective role)
  const storageKey =
    onboardingRole &&
    (['agent', 'supervisor', 'admin'].includes(onboardingRole) || isOwner)
      ? `aica-onboarding-${isOwner && !viewAsRole ? 'admin' : onboardingRole}`
      : 'aica-onboarding-temp';

  // Use state to track progress from OnboardingChecklist component
  const [onboardingProgress, setOnboardingProgress] = useState<{
    completed: number;
    total: number;
    percentage: number;
    isComplete: boolean;
  } | null>(null);

  // Note: Product tour is now handled by OnboardingTour component
  // triggered from Topbar help button (using react-joyride)

  // Check if user has seen onboarding checklist
  useEffect(() => {
    if (!onboardingRole) return;

    const onboardingKey = `aica-onboarding-${onboardingRole}-seen`;
    const hasSeenOnboarding = localStorage.getItem(onboardingKey);

    logDebug('dashboard.onboarding.check_visibility', {
      onboardingRole,
      viewAsRole,
      globalRole,
      hasSeenOnboarding,
      shouldShow:
        !hasSeenOnboarding &&
        onboardingRole &&
        ['agent', 'supervisor', 'admin'].includes(onboardingRole),
    });

    // Show onboarding for owner/admin/agent/supervisor roles if not seen
    // When in View As mode, show for effective role
    if (
      (onboardingRole && ['agent', 'supervisor', 'admin'].includes(onboardingRole)) ||
      (isOwner && !viewAsRole)
    ) {
      if (!hasSeenOnboarding) {
        startTransition(() => {
          setShowOnboarding(true);
        });
      }
    }
  }, [onboardingRole, globalRole, viewAsRole, isOwner]);

  // Handle progress updates from OnboardingChecklist
  const handleProgressChange = (progress: typeof onboardingProgress) => {
    setOnboardingProgress(progress);
    if (progress) {
      logDebug('dashboard.onboarding.progress_updated', {
        completed: progress.completed,
        total: progress.total,
        isComplete: progress.isComplete,
        collapsed: onboardingCollapsed,
        role: globalRole,
      });
    }
  };

  // Auto-collapse when all tasks completed (use progress from OnboardingChecklist)
  useEffect(() => {
    if (!onboardingProgress) {
      return;
    }

    if (onboardingProgress.isComplete && !onboardingCollapsed && onboardingRole) {
      logDebug('dashboard.onboarding.auto_collapse_scheduled', {
        role: onboardingRole,
        viewAsMode: !!viewAsRole,
      });

      // Auto-collapse after a short delay when all tasks are complete
      const timer = setTimeout(() => {
        logDebug('dashboard.onboarding.auto_collapse_execute', {
          role: onboardingRole,
          viewAsMode: !!viewAsRole,
        });
        startTransition(() => {
          setOnboardingCollapsed(true);
        });
        const collapsedKey = `aica-onboarding-${onboardingRole}-collapsed`;
        try {
          localStorage.setItem(collapsedKey, 'true');
        } catch (e) {
          if (e instanceof DOMException && e.code === DOMException.QUOTA_EXCEEDED_ERR) {
            logWarning('dashboard.onboarding.local_storage_quota_exceeded', {
              role: onboardingRole,
            });
          }
        }
      }, 2000); // 2 second delay to show completion message

      return () => clearTimeout(timer);
    }
  }, [onboardingProgress, onboardingCollapsed, onboardingRole, viewAsRole]);

  // Load collapsed state from localStorage (only on initial mount, not on every role change)
  useEffect(() => {
    if (!onboardingRole) return;
    const collapsedKey = `aica-onboarding-${onboardingRole}-collapsed`;
    const saved = localStorage.getItem(collapsedKey);
    logDebug('dashboard.onboarding.load_collapsed_state', {
      collapsedKey,
      saved,
      viewAsMode: !!viewAsRole,
    });
    startTransition(() => {
      setOnboardingCollapsed(saved === 'true');
    });
  }, [onboardingRole, viewAsRole]);

  // Save collapsed state to localStorage
  const handleToggleCollapse = () => {
    const newCollapsed = !onboardingCollapsed;
    startTransition(() => {
      setOnboardingCollapsed(newCollapsed);
    });
    if (onboardingRole) {
      const collapsedKey = `aica-onboarding-${onboardingRole}-collapsed`;
      try {
        localStorage.setItem(collapsedKey, String(newCollapsed));
      } catch (e) {
        if (e instanceof DOMException && e.code === DOMException.QUOTA_EXCEEDED_ERR) {
          logWarning('dashboard.onboarding.collapsed_state_not_saved_quota_exceeded', {
            role: onboardingRole,
          });
        } else {
          logError(
            'dashboard.onboarding.save_collapsed_state_failed',
            { role: onboardingRole },
            e instanceof Error ? e : undefined
          );
        }
      }
    }
  };

  // Render role-specific dashboard
  const renderDashboard = () => {
    if (roleLoading) {
      return <DashboardSkeleton />;
    }

    // Platform owner gets dedicated dashboard (unless in View As mode)
    if (isOwner && !viewAsRole) {
      return <OwnerDashboard />; // Owner = platform super-admin, billing/users/system health focus
    }

    if (unifiedShown && unifiedView) {
      return <UnifiedDashboardPage view={unifiedView} />;
    }

    // The effective role's dashboard (viewAsRole || globalRole) — or, once
    // `/access/me` says so, the one a grant covers (`dashboardFor`, #1645).
    // Deliberate: while `/access/me` loads, the role's dashboard renders at
    // once and is swapped when the answer is `managed` — no wait for the many
    // without a grant, and the API refuses (or projects) every read of the
    // wrong dashboard for the few with one.
    switch (shownDashboard) {
      case 'client':
        return <ClientDashboard />;
      case 'admin':
        return <AdminDashboard />;
      case 'agent':
        return <AgentDashboard />;
      case 'supervisor':
        return <SupervisorDashboard managedAccess={managedAccessOf(access) !== null} />;
      default:
        return (
          <div
            data-testid="dashboard-role-error"
            className="flex h-full items-center justify-center"
          >
            <p className="text-fg-muted">{t('unableToDetermineRole')}</p>
          </div>
        );
    }
  };

  const shouldRenderOnboarding =
    showOnboarding &&
    dashboardKnown &&
    ((onboardingRole && ['agent', 'supervisor', 'admin'].includes(onboardingRole)) ||
      (isOwner && !viewAsRole));

  logDebug('dashboard.onboarding.sidebar_render_check', {
    showOnboarding,
    onboardingRole,
    globalRole,
    viewAsRole,
    isOwner,
    shouldRender: shouldRenderOnboarding,
  });

  // The tour of the dashboard on screen (#1677): its steps point at that
  // dashboard's widgets, and a step whose sidebar item the subject is not
  // offered drops out by itself (OnboardingTour skips absent targets). Not
  // mounted until the dashboard on screen is known (`dashboardKnown`).
  const tourType =
    shownDashboard === 'agent'
      ? 'agent'
      : shownDashboard === 'supervisor'
        ? 'supervisor'
        : 'admin';

  return (
    <div data-testid="dashboard-page" className="flex min-h-full bg-bg">
      {/* Product tour — mounts silently, auto-starts if not completed */}
      {dashboardKnown && <OnboardingTour tour={tourType} screenSettled={flagsSettled} />}
      {/* Main dashboard content */}
      <div data-testid="dashboard-main-content" className="flex-1 overflow-auto bg-bg">
        {/* Compliance Warning Banner */}
        {!complianceConfirmed && (
          <div className="px-6 pt-4">
            <div className="rounded-lg border border-border bg-warning-dim p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
                <p className="text-sm text-fg">
                  {t('settings:compliance.dashboardBanner')}
                </p>
              </div>
              <Link
                to="/settings"
                className="text-sm font-medium text-primary hover:text-primary transition-colors shrink-0 ml-4"
              >
                {t('settings:compliance.dashboardBannerAction')}{' '}
                <ArrowRight className="h-3 w-3 inline ml-1" />
              </Link>
            </div>
          </div>
        )}
        <div className="px-6 pt-4">
          <DemoDataCard />
        </div>
        <Suspense fallback={<DashboardSkeleton />}>
          <AnimatedContainer direction="up" duration="moderate">
            {renderDashboard()}
          </AnimatedContainer>
        </Suspense>
      </div>

      {/* Onboarding Checklist — collapsed: fixed floating button, expanded: inline sidebar */}
      {shouldRenderOnboarding && onboardingCollapsed && (
        <button
          data-testid="dashboard-onboarding-collapse-button"
          onClick={handleToggleCollapse}
          className={cn(
            'fixed right-0 top-1/2 -translate-y-1/2 z-40',
            'flex flex-col items-center gap-1 py-3 px-1.5',
            'bg-bg-elevated border border-border border-r-0 rounded-l-md shadow-md',
            'text-fg-muted hover:text-fg transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
          aria-label={t('expandChecklist')}
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          {onboardingProgress && !onboardingProgress.isComplete && (
            <span className="text-2xs text-fg-muted tabular-nums">
              {onboardingProgress.completed}/{onboardingProgress.total}
            </span>
          )}
        </button>
      )}
      {shouldRenderOnboarding && !onboardingCollapsed && (
        <aside
          data-testid="dashboard-onboarding-sidebar"
          className="border-l border-border transition-all duration-150 bg-bg-elevated w-80 shrink-0"
        >
          {/* Header */}
          <div className="flex items-center justify-end gap-1 p-2 border-b border-border bg-bg-elevated rounded-tr-md">
            <button
              data-testid="dashboard-onboarding-dismiss-button"
              onClick={() => {
                logDebug('dashboard.onboarding.dismiss_clicked', {
                  role: onboardingRole,
                });
                if (onboardingRole) {
                  try {
                    localStorage.setItem(
                      `aica-onboarding-${onboardingRole}-seen`,
                      'true'
                    );
                  } catch (e) {
                    if (
                      e instanceof DOMException &&
                      e.code === DOMException.QUOTA_EXCEEDED_ERR
                    ) {
                      logWarning('dashboard.onboarding.local_storage_quota_exceeded', {
                        role: onboardingRole,
                      });
                    }
                  }
                }
                setShowOnboarding(false);
              }}
              className={cn(
                CONTROL_SQUARE.md,
                'rounded-sm p-2 flex items-center justify-center',
                'text-fg hover:text-fg hover:bg-bg-surface',
                'transition-all duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'border border-border'
              )}
              aria-label={t('dismissChecklist')}
              title={t('hideGettingStarted')}
            >
              <X className="h-4 w-4" />
            </button>
            <button
              data-testid="dashboard-onboarding-collapse-button"
              onClick={handleToggleCollapse}
              className={cn(
                CONTROL_SQUARE.md,
                'rounded-sm p-2 flex items-center justify-center text-fg-muted transition-all duration-150',
                'hover:bg-bg-surface hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              aria-label={t('collapseChecklist')}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Checklist content */}
          <div data-testid="dashboard-onboarding-content" className="p-4">
            <OnboardingChecklist
              role={
                (isOwner && !viewAsRole ? 'admin' : onboardingRole) as
                  'agent' | 'supervisor' | 'admin'
              }
              storageKey={storageKey}
              onProgressChange={handleProgressChange}
              onComplete={() => {
                try {
                  localStorage.setItem(`aica-onboarding-${onboardingRole}-seen`, 'true');
                } catch (e) {
                  if (
                    e instanceof DOMException &&
                    e.code === DOMException.QUOTA_EXCEEDED_ERR
                  ) {
                    logWarning('dashboard.onboarding.local_storage_quota_exceeded', {
                      role: onboardingRole,
                    });
                  }
                }
                setShowOnboarding(false);
              }}
            />
          </div>
        </aside>
      )}
    </div>
  );
}

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});
